import { Theme } from '@mui/material/styles';

const components = {
  MuiCssBaseline: {
    styleOverrides: (theme: Theme) => ({
      // Dark mode scrollbar styles
      ...(theme.palette.mode === 'dark' && {
        '*::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '*::-webkit-scrollbar-track': {
          background: theme.palette.background.default,
        },
        '*::-webkit-scrollbar-thumb': {
          background: theme.palette.primary.main,
          borderRadius: '4px',
          '&:hover': {
            background: `${theme.palette.primary.main}cc`,
          },
        },
        // Firefox scrollbar
        '*': {
          scrollbarWidth: 'thin',
          scrollbarColor: `${theme.palette.primary.main} ${theme.palette.background.default}`,
        },
      }),
    }),
  },
  MuiButtonBase: {
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiButton: {
    styleOverrides: {
      contained: ({ theme }: { theme: Theme }) => ({
        boxShadow: 'rgba(0, 0, 0, 0.05) 0 2px 4px 0',
        color: theme.palette.common.white,
      }),
      // Text buttons (like "Back" button) in dark mode
      text: ({ theme }: { theme: Theme }) => ({
        ...(theme.palette.mode === 'dark' && {
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: `${theme.palette.primary.main}20`,
          },
        }),
      }),
      // Outlined buttons in dark mode
      outlined: ({ theme }: { theme: Theme }) => ({
        ...(theme.palette.mode === 'dark' && {
          color: theme.palette.primary.contrastText,
          borderColor: theme.palette.primary.contrastText,
          '&:hover': {
            borderColor: theme.palette.primary.contrastText,
            backgroundColor: `${theme.palette.primary.main}20`,
          },
        }),
      }),
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        // In dark mode, use the lighter primary color for icon buttons
        ...(theme.palette.mode === 'dark' && {
          color: theme.palette.primary.contrastText,
          '&:hover': {
            backgroundColor: `${theme.palette.primary.main}30`,
          },
        }),
      }),
      colorPrimary: ({ theme }: { theme: Theme }) => ({
        ...(theme.palette.mode === 'dark' && {
          color: theme.palette.primary.contrastText,
        }),
      }),
    },
  },
  MuiLink: {
    defaultProps: {
      underline: 'hover',
    },
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        // In dark mode, use the lighter primary color for links
        ...(theme.palette.mode === 'dark' && {
          color: theme.palette.primary.contrastText,
          '&:hover': {
            color: theme.palette.primary.contrastText,
            opacity: 0.8,
          },
        }),
      }),
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        // In dark mode, use the lighter primary color for tab text
        ...(theme.palette.mode === 'dark' && {
          '&.Mui-selected': {
            color: theme.palette.primary.contrastText,
          },
        }),
      }),
    },
  },
  MuiCardHeader: {
    defaultProps: {
      titleTypographyProps: {
        variant: 'h6',
      },
    },
    styleOverrides: {
      action: {
        marginTop: '-4px',
        marginRight: '-4px',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: '6px',
        boxShadow:
          'rgba(50, 50, 93, 0.025) 0px 2px 5px -1px, rgba(0, 0, 0, 0.05) 0px 1px 3px -1px',
        backgroundImage: 'none',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        borderRadius: 0,
      },
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      day: {
        fontWeight: '300',
      },
    },
  },
  MuiPickersYear: {
    styleOverrides: {
      root: {
        height: '64px',
      },
    },
  },
  MuiPickersCalendar: {
    styleOverrides: {
      transitionContainer: {
        marginTop: '6px',
      },
    },
  },
  MuiPickersCalendarHeader: {
    styleOverrides: {
      iconButton: {
        backgroundColor: 'transparent',
        '& > *': {
          backgroundColor: 'transparent',
        },
      },
      switchHeader: {
        marginTop: '2px',
        marginBottom: '4px',
      },
    },
  },
  MuiPickersClock: {
    styleOverrides: {
      container: {
        margin: '32px 0 4px',
      },
    },
  },
  MuiPickersClockNumber: {
    styleOverrides: {
      clockNumber: {
        left: 'calc(50% - 16px)',
        width: '32px',
        height: '32px',
      },
    },
  },
  MuiPickerDTHeader: {
    styleOverrides: {
      dateHeader: {
        '& h4': {
          fontSize: '2.125rem',
          fontWeight: 400,
        },
      },
      timeHeader: {
        '& h3': {
          fontSize: '3rem',
          fontWeight: 400,
        },
      },
    },
  },
  MuiPickersTimePicker: {
    styleOverrides: {
      hourMinuteLabel: {
        '& h2': {
          fontSize: '3.75rem',
          fontWeight: 300,
        },
      },
    },
  },
  MuiPickersToolbar: {
    styleOverrides: {
      toolbar: {
        '& h4': {
          fontSize: '2.125rem',
          fontWeight: 400,
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: '6px',
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }: { theme: Theme }) => ({
        border: '1px solid red',
        borderColor: theme.palette.divider,
      }),
    },
  },
};

export default components;
